import { supabase } from './supabase'
import {
  formatDate, formatMoney, formatPeriod, periodsOfYear, occupancyDuration,
} from './format'
import { daysLate } from './rules'
import { computeWaterfall, chargesInForce, type RecurringCharge } from './waterfall'
import type { StringKey } from '../i18n/strings'

/**
 * DEFINITION DES RAPPORTS.
 *
 * Chaque rapport est un descripteur : un titre, s'il depend d'un mois,
 * et une fonction qui renvoie ses colonnes et ses lignes. Ajouter un
 * rapport, c'est ajouter un descripteur ici, pas un ecran.
 *
 * Chaque colonne porte deux formats : ce qui s'affiche a l'ecran, et
 * ce qui part dans le fichier Excel. Un montant s'affiche
 * « 1 719 000 F CFA » et s'exporte 1719000, sinon Excel n'en fait pas
 * un nombre.
 */

export interface ReportColumn {
  label: string
  /** Affichage a l'ecran. */
  display: (row: Record<string, unknown>) => string
  /** Valeur pour Excel : nombre brut pour un montant. */
  raw: (row: Record<string, unknown>) => string | number | null
  align?: 'right'
  /** Colonne a totaliser en pied de tableau. */
  sum?: boolean
}

export interface ReportResult {
  columns: ReportColumn[]
  rows: Record<string, unknown>[]
  /** Ligne d'en-tete supplementaire, en clair. */
  note?: string
  /**
   * Vrai si chaque ligne porte un champ « period » : le rapport peut
   * alors etre regroupe par mois quand on demande une annee entiere.
   */
  groupable?: boolean
}

/**
 * Etendue demandee : un mois, ou une annee entiere.
 *
 * Une annee est ici les douze periodes portant son millesime, de
 * AAAA-01 a AAAA-12. Chaque periode courant du 5 au 5, l'annee 2026 va
 * donc du 05/01/2026 au 05/01/2027. C'est la lecture qui correspond au
 * tableau papier ; une annee fiscale differente se traiterait a part.
 */
export interface ReportRange {
  mode: 'month' | 'year'
  /** Mois demande, en mode mois. */
  period: string
  /** Annee demandee, en mode annee. */
  year: number
  /** Les periodes couvertes : une seule, ou les douze de l'annee. */
  periods: string[]
}

export function monthRange(period: string): ReportRange {
  return {
    mode: 'month',
    period,
    year: Number(period.slice(0, 4)),
    periods: [period],
  }
}

export function yearRange(year: number): ReportRange {
  return {
    mode: 'year',
    period: `${year}-01`,
    year,
    periods: Array.from(
      { length: 12 },
      (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`,
    ),
  }
}

export interface ReportDef {
  key: string
  titleKey: StringKey
  descriptionKey: StringKey
  /** Le rapport porte-t-il sur une periode ? */
  monthly: boolean
  load: (range: ReportRange, t: Translate) => Promise<ReportResult>
}

/**
 * Decoupe les lignes par mois, dans l'ordre chronologique.
 *
 * Hors du composant, volontairement : le regroupement et les
 * sous-totaux portent des chiffres d'argent, donc ils se testent.
 */
export function groupByPeriod(
  rows: Record<string, unknown>[],
): [string, Record<string, unknown>[]][] {
  const map = new Map<string, Record<string, unknown>[]>()
  for (const r of rows) {
    const cle = String(r.period ?? '')
    const liste = map.get(cle) ?? []
    liste.push(r)
    map.set(cle, liste)
  }
  return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]))
}

/** Somme d'une colonne, 0 si elle n'est pas totalisable. */
export function sumColumn(
  rows: Record<string, unknown>[],
  column: ReportColumn,
): number {
  return rows.reduce((s, r) => s + Number(column.raw(r) ?? 0), 0)
}

type Translate = (key: StringKey, vars?: Record<string, string | number>) => string

const money = (key: string, label: string, sum = true): ReportColumn => ({
  label,
  display: (r) => formatMoney(Number(r[key] ?? 0)),
  raw: (r) => Math.round(Number(r[key] ?? 0)),
  align: 'right',
  sum,
})

const text = (key: string, label: string): ReportColumn => ({
  label,
  display: (r) => String(r[key] ?? '—'),
  raw: (r) => (r[key] === null || r[key] === undefined ? '' : String(r[key])),
})

const date = (key: string, label: string): ReportColumn => ({
  label,
  display: (r) => (r[key] ? formatDate(String(r[key])) : '—'),
  raw: (r) => (r[key] ? formatDate(String(r[key])) : ''),
})

// ---------------------------------------------------------------------

/** 1. Versements de loyer du mois, un versement par ligne. */
const rentPayments: ReportDef = {
  key: 'rent_payments',
  titleKey: 'report_rent_payments',
  descriptionKey: 'report_rent_payments_desc',
  monthly: true,
  async load(range, t) {
    const { data, error } = await supabase
      .from('payments')
      .select(
        'id, paid_on, amount, method, receipt_number, period,' +
          ' charge:rent_charges(period, lease:leases(' +
          'unit:units(name), tenant:tenants(full_name)))',
      )
      .in('period', range.periods)
      .order('paid_on', { ascending: true })
    if (error) throw error

    const rows = (data ?? []).map((p) => {
      const row = p as unknown as Record<string, unknown>
      const charge = row.charge as
        | { lease?: { unit?: { name: string }; tenant?: { full_name: string } } }
        | null
      return {
        period: row.period,
        unit: charge?.lease?.unit?.name ?? '',
        tenant: charge?.lease?.tenant?.full_name ?? '',
        paid_on: row.paid_on,
        amount: row.amount,
        method: t(`payment_method_${String(row.method)}` as StringKey),
        receipt: row.receipt_number ?? '',
        late: daysLate(String(row.period), String(row.paid_on)),
      }
    })

    return {
      columns: [
        text('unit', t('lease_unit')),
        text('tenant', t('lease_tenant')),
        date('paid_on', t('payment_date')),
        money('amount', t('payment_amount')),
        text('method', t('payment_method')),
        text('receipt', t('payment_receipt_number')),
        {
          label: t('report_days_late'),
          display: (r) => (Number(r.late) > 0 ? String(r.late) : '—'),
          raw: (r) => Number(r.late ?? 0),
          align: 'right',
        },
      ],
      rows,
      groupable: true,
    }
  },
}

/** 2. Etat des loyers du mois : attendu, recu, reste. */
const rentStatus: ReportDef = {
  key: 'rent_status',
  titleKey: 'report_rent_status',
  descriptionKey: 'report_rent_status_desc',
  monthly: true,
  async load(range, t) {
    const { data, error } = await supabase
      .from('v_rent_status')
      .select('*')
      .in('period', range.periods)
      .order('period')
      .order('unit_name')
    if (error) throw error

    return {
      columns: [
        text('unit_name', t('lease_unit')),
        text('tenant_name', t('lease_tenant')),
        money('amount_due', t('rent_expected')),
        money('amount_received', t('rent_received')),
        money('balance', t('rent_balance')),
        {
          label: t('lease_status'),
          display: (r) => t(`rent_status_${String(r.status)}` as StringKey),
          raw: (r) => t(`rent_status_${String(r.status)}` as StringKey),
        },
      ],
      rows: (data ?? []) as unknown as Record<string, unknown>[],
      groupable: true,
    }
  },
}

/** 3. Arrieres cumules par locataire, tous mois confondus. */
const arrears: ReportDef = {
  key: 'arrears',
  titleKey: 'report_arrears',
  descriptionKey: 'report_arrears_desc',
  monthly: false,
  async load(_range, t) {
    const { data, error } = await supabase
      .from('v_tenant_arrears')
      .select('*')
      .order('arrears_total', { ascending: false })
    if (error) throw error

    const rows = (data ?? []).filter(
      (r) => Number((r as Record<string, unknown>).arrears_total ?? 0) > 0,
    ) as unknown as Record<string, unknown>[]

    return {
      columns: [
        text('tenant_name', t('lease_tenant')),
        money('arrears_total', t('rent_balance')),
        {
          label: t('report_months_owing'),
          display: (r) => String(r.months_owing ?? 0),
          raw: (r) => Number(r.months_owing ?? 0),
          align: 'right',
        },
        text('oldest_unpaid_period', t('report_oldest_unpaid')),
      ],
      rows,
    }
  },
}

/** 4. Locataires, leur unite et leur bail. */
const tenants: ReportDef = {
  key: 'tenants',
  titleKey: 'report_tenants',
  descriptionKey: 'report_tenants_desc',
  monthly: false,
  async load(_range, t) {
    const { data, error } = await supabase
      .from('leases')
      .select(
        'id, rent_ttc, rent_ht, tva_rate, start_date, end_date, status,' +
          ' a_verifier, unit:units(name, kind), tenant:tenants(full_name, phone)',
      )
      .in('status', ['actif', 'expire'])
    if (error) throw error

    const rows = (data ?? []).map((l) => {
      const row = l as unknown as Record<string, unknown>
      const unit = row.unit as { name: string; kind: string } | null
      const tenant = row.tenant as { full_name: string; phone: string | null } | null

      // Duree d'occupation. Null si la date de debut est inconnue :
      // c'est le cas des baux marques a verifier, et un zero serait
      // faux.
      const d = occupancyDuration(
        (row.start_date as string | null) ?? null,
        (row.end_date as string | null) ?? null,
      )

      return {
        unit: unit?.name ?? '',
        kind: t(unit?.kind === 'commercial'
          ? 'unit_kind_commercial'
          : 'unit_kind_habitation'),
        tenant: tenant?.full_name ?? '',
        phone: tenant?.phone ?? '',
        rent_ttc: row.rent_ttc,
        start_date: row.start_date,
        end_date: row.end_date,
        // Deux colonnes : les annees seules pour trier et sommer dans
        // Excel, et la duree en clair pour la lire.
        annees: d === null ? null : d.years,
        duree: d === null
          ? ''
          : d.years === 0
            ? `${d.months} mois`
            : d.months === 0
              ? `${d.years} an${d.years > 1 ? 's' : ''}`
              : `${d.years} an${d.years > 1 ? 's' : ''} ${d.months} mois`,
        status: t(`lease_status_${String(row.status)}` as StringKey),
      }
    })

    return {
      columns: [
        text('unit', t('lease_unit')),
        text('kind', t('unit_kind')),
        text('tenant', t('lease_tenant')),
        text('phone', t('tenant_phone')),
        money('rent_ttc', t('lease_rent_ttc')),
        date('start_date', t('lease_start')),
        date('end_date', t('lease_end')),
        {
          label: t('report_years_occupied'),
          display: (r) => (r.annees === null ? '—' : String(r.annees)),
          raw: (r) => (r.annees === null ? '' : Number(r.annees)),
          align: 'right',
        },
        text('duree', t('report_occupancy')),
        text('status', t('lease_status')),
      ],
      rows,
    }
  },
}

/** 5. Cautions : montant, detenteur, preuve. */
const deposits: ReportDef = {
  key: 'deposits',
  titleKey: 'report_deposits',
  descriptionKey: 'report_deposits_desc',
  monthly: false,
  async load(_range, t) {
    const { data, error } = await supabase
      .from('deposits')
      .select(
        'id, kind, amount, received_on, held_by, status, proof_document,' +
          ' lease:leases(unit:units(name), tenant:tenants(full_name))',
      )
      .order('status')
    if (error) throw error

    const rows = (data ?? []).map((d) => {
      const row = d as unknown as Record<string, unknown>
      const lease = row.lease as
        | { unit?: { name: string }; tenant?: { full_name: string } }
        | null
      return {
        tenant: lease?.tenant?.full_name ?? '',
        unit: lease?.unit?.name ?? '',
        kind: t(`deposit_kind_${String(row.kind)}` as StringKey),
        amount: row.amount,
        received_on: row.received_on,
        held_by: t(`holder_${String(row.held_by)}` as StringKey),
        status: t(`deposit_status_${String(row.status)}` as StringKey),
        proof: row.proof_document ? t('photo_attached') : t('deposit_no_proof'),
      }
    })

    return {
      columns: [
        text('tenant', t('lease_tenant')),
        text('unit', t('lease_unit')),
        text('kind', t('deposit_kind')),
        money('amount', t('deposit_amount')),
        date('received_on', t('deposit_received_on')),
        text('held_by', t('deposit_held_by')),
        text('status', t('deposit_status')),
        text('proof', t('deposit_proof')),
      ],
      rows,
    }
  },
}

/** 6. Depenses du mois, avec leur etat. */
const expenses: ReportDef = {
  key: 'expenses',
  titleKey: 'report_expenses',
  descriptionKey: 'report_expenses_desc',
  monthly: true,
  async load(range, t) {
    const { data, error } = await supabase
      .from('expenses')
      .select(
        'id, period, spent_on, category, amount, supplier, paid_by, status,' +
          ' tenant_charge, invoice_document, description, unit:units(name)',
      )
      .in('period', range.periods)
      .order('spent_on')
    if (error) throw error

    const rows = (data ?? []).map((e) => {
      const row = e as unknown as Record<string, unknown>
      const unit = row.unit as { name: string } | null
      return {
        period: row.period,
        spent_on: row.spent_on,
        category: t(`cat_${String(row.category)}` as StringKey),
        unit: unit?.name ?? t('expense_whole_building'),
        supplier: row.supplier ?? '',
        amount: row.amount,
        paid_by: t(`payer_${String(row.paid_by)}` as StringKey),
        status: t(`expense_status_${String(row.status)}` as StringKey),
        tenant_charge: row.tenant_charge ? t('tenant_charge_flag') : '',
        invoice: row.invoice_document ? t('photo_attached') : t('deposit_no_proof'),
        description: row.description ?? '',
      }
    })

    return {
      columns: [
        date('spent_on', t('expense_date')),
        text('category', t('expense_category')),
        text('unit', t('expense_unit')),
        money('amount', t('expense_amount')),
        text('supplier', t('expense_supplier')),
        text('paid_by', t('expense_paid_by')),
        text('status', t('lease_status')),
        text('tenant_charge', t('tenant_charge_flag')),
        text('invoice', t('expense_invoice')),
        text('description', t('expense_description')),
      ],
      rows,
      groupable: true,
    }
  },
}

/** 7. Depenses par categorie : la synthese du mois. */
const expensesByCategory: ReportDef = {
  key: 'expenses_by_category',
  titleKey: 'report_expenses_category',
  descriptionKey: 'report_expenses_category_desc',
  monthly: true,
  async load(range, t) {
    const { data, error } = await supabase
      .from('expenses')
      .select('period, category, amount, status')
      .in('period', range.periods)
    if (error) throw error

    // Regroupement par mois ET par categorie : sur une annee entiere,
    // on veut savoir quel mois a porte quelle depense, pas seulement le
    // total annuel par categorie.
    const cles = new Map<
      string,
      { period: string; category: string; approuve: number; attente: number; n: number }
    >()
    for (const e of (data ?? []) as {
      period: string; category: string; amount: number; status: string
    }[]) {
      const cle = `${e.period}|${e.category}`
      const cur =
        cles.get(cle) ??
        { period: e.period, category: e.category, approuve: 0, attente: 0, n: 0 }
      if (e.status === 'approuve') cur.approuve += e.amount
      if (e.status === 'en_attente') cur.attente += e.amount
      cur.n += 1
      cles.set(cle, cur)
    }

    const rows = [...cles.values()]
      .sort((a, b) =>
        a.period === b.period
          ? a.category.localeCompare(b.category)
          : a.period.localeCompare(b.period),
      )
      .map((v) => ({
        period: v.period,
        category: t(`cat_${v.category}` as StringKey),
        approuve: v.approuve,
        attente: v.attente,
        n: v.n,
      }))

    return {
      columns: [
        text('category', t('expense_category')),
        money('approuve', t('expense_total_approved')),
        money('attente', t('expense_total_pending')),
        {
          label: t('report_count'),
          display: (r) => String(r.n ?? 0),
          raw: (r) => Number(r.n ?? 0),
          align: 'right',
        },
      ],
      rows,
      groupable: true,
    }
  },
}

/** 8. Loyer theorique de l'immeuble : ce que produit un mois plein. */
const rentRoll: ReportDef = {
  key: 'rent_roll',
  titleKey: 'report_rent_roll',
  descriptionKey: 'report_rent_roll_desc',
  monthly: false,
  async load(_range, t) {
    const { data, error } = await supabase
      .from('leases')
      .select(
        'rent_ht, rent_ttc, tva_rate, status,' +
          ' unit:units(name, kind), tenant:tenants(full_name)',
      )
      .in('status', ['actif', 'expire'])
    if (error) throw error

    const rows = (data ?? []).map((l) => {
      const row = l as unknown as Record<string, unknown>
      const unit = row.unit as { name: string; kind: string } | null
      const tenant = row.tenant as { full_name: string } | null
      const ttc = Number(row.rent_ttc ?? 0)
      const ht = Number(row.rent_ht ?? 0)
      return {
        unit: unit?.name ?? '',
        kind: t(unit?.kind === 'commercial'
          ? 'unit_kind_commercial'
          : 'unit_kind_habitation'),
        tenant: tenant?.full_name ?? '',
        rent_ht: ht,
        tva: ttc - ht,
        rent_ttc: ttc,
        annual: ttc * 12,
      }
    })

    return {
      columns: [
        text('unit', t('lease_unit')),
        text('kind', t('unit_kind')),
        text('tenant', t('lease_tenant')),
        money('rent_ht', t('lease_ht')),
        money('tva', t('lease_tva_amount')),
        money('rent_ttc', t('lease_rent_ttc')),
        money('annual', t('report_annual')),
      ],
      rows,
      note: t('report_rent_roll_note'),
    }
  },
}

/** 9. Inventaire des pieces justificatives. */
const documents: ReportDef = {
  key: 'documents',
  titleKey: 'report_documents',
  descriptionKey: 'report_documents_desc',
  monthly: false,
  async load(_range, t) {
    const { data, error } = await supabase
      .from('documents')
      .select('id, kind, label, period, uploaded_at, byte_size, unit:units(name)')
      .order('uploaded_at', { ascending: false })
      .limit(500)
    if (error) throw error

    const rows = (data ?? []).map((d) => {
      const row = d as unknown as Record<string, unknown>
      const unit = row.unit as { name: string } | null
      return {
        kind: t(`doc_kind_${String(row.kind)}` as StringKey),
        unit: unit?.name ?? '',
        period: row.period ? formatPeriod(String(row.period), 'fr') : '',
        uploaded_at: String(row.uploaded_at).slice(0, 10),
        size: row.byte_size ? Math.round(Number(row.byte_size) / 1024) : 0,
        id: row.id,
      }
    })

    return {
      columns: [
        text('kind', t('doc_kind')),
        text('unit', t('lease_unit')),
        text('period', t('report_period')),
        date('uploaded_at', t('doc_uploaded_at')),
        {
          label: t('doc_size'),
          display: (r) => `${r.size} Ko`,
          raw: (r) => Number(r.size ?? 0),
          align: 'right',
        },
      ],
      rows,
    }
  },
}

/**
 * 10. Le revenu de chaque mois de l'annee.
 *
 * Une ligne par mois : loyers encaisses, chaque charge mensuelle
 * deduite, les depenses, et ce qui reste. Douze lignes pour voir
 * l'annee d'un coup, et un total en pied.
 *
 * Les colonnes de charges sont CONSTRUITES a partir de ce qui est
 * configure, et non ecrites en dur : ajouter une quatrieme charge
 * mensuelle ajoutera sa colonne toute seule.
 *
 * LES DEPENSES EN ATTENTE SONT MONTREES MAIS NON DEDUITES. Votre
 * cahier des charges est clair : seules les depenses approuvees
 * reduisent le net. Les masquer serait pire, on ne saurait pas ce qui
 * attend une decision ; les deduire serait faux.
 */
const revenueByMonth: ReportDef = {
  key: 'revenue_by_month',
  titleKey: 'report_revenue',
  descriptionKey: 'report_revenue_desc',
  monthly: false,
  async load(range, t) {
    const periods = periodsOfYear(range.year)

    const [rent, expenses, charges] = await Promise.all([
      supabase
        .from('v_rent_status')
        .select('period, amount_received')
        .in('period', periods),
      supabase
        .from('expenses')
        .select('period, amount, status')
        .in('period', periods),
      supabase
        .from('recurring_charges')
        .select(
          'code, label_fr, label_en, kind, rate, fixed_amount, base, payee,' +
            ' requires_receipt, sort_order, a_verifier,' +
            ' effective_from, effective_to, active',
        )
        .order('sort_order'),
    ])

    if (rent.error) throw rent.error
    if (expenses.error) throw expenses.error
    if (charges.error) throw charges.error

    const encaisse = new Map<string, number>()
    for (const r of (rent.data ?? []) as { period: string; amount_received: number }[]) {
      encaisse.set(r.period, (encaisse.get(r.period) ?? 0) + Number(r.amount_received))
    }

    const approuve = new Map<string, number>()
    const attente = new Map<string, number>()
    for (const e of (expenses.data ?? []) as {
      period: string; amount: number; status: string
    }[]) {
      const cible = e.status === 'approuve' ? approuve : e.status === 'en_attente' ? attente : null
      if (cible) cible.set(e.period, (cible.get(e.period) ?? 0) + Number(e.amount))
    }

    type ChargeRow = RecurringCharge & {
      effective_from: string; effective_to: string | null; active: boolean
      label_fr: string; label_en: string
    }
    const toutes = (charges.data ?? []) as unknown as ChargeRow[]

    // Les codes de charge rencontres sur l'annee, dans l'ordre de la
    // cascade : ce sont les colonnes du tableau.
    const codes: { code: string; label: string; order: number }[] = []
    for (const c of toutes) {
      if (!codes.some((x) => x.code === c.code)) {
        codes.push({
          // Libelle francais : les rapports sont imprimes, et le
          // francais est la langue de tout ce qui est imprime ou signe.
          code: c.code,
          label: c.label_fr,
          order: c.sort_order,
        })
      }
    }
    codes.sort((a, b) => a.order - b.order)

    const rows = periods.map((p) => {
      const collected = encaisse.get(p) ?? 0
      const enForce = chargesInForce(toutes, p).map((c) => ({
        ...c,
        label: c.label_fr,
        rate: c.rate === null ? null : Number(c.rate),
      }))
      const w = computeWaterfall(collected, enForce, approuve.get(p) ?? 0)

      const ligne: Record<string, unknown> = {
        period: p,
        rent_collected: collected,
        charges_total: w.recurringTotal,
        expenses_approved: w.expensesApproved,
        expenses_pending: attente.get(p) ?? 0,
        net: w.netToDeposit,
      }
      // Une colonne par charge, vide si la charge ne s'appliquait pas
      // encore ce mois-la.
      for (const c of codes) {
        ligne[`charge_${c.code}`] =
          w.lines.find((l) => l.code === c.code)?.amount ?? 0
      }
      return ligne
    })

    return {
      columns: [
        {
          label: t('report_period'),
          display: (r) => formatPeriod(String(r.period), 'fr'),
          raw: (r) => String(r.period),
        },
        money('rent_collected', t('settlement_rent_collected')),
        ...codes.map((c) => money(`charge_${c.code}`, c.label)),
        money('charges_total', t('report_charges_total')),
        money('expenses_approved', t('expense_total_approved')),
        money('expenses_pending', t('expense_total_pending')),
        money('net', t('report_net_revenue')),
      ],
      rows,
      note: t('report_revenue_note'),
    }
  },
}

export const REPORTS: ReportDef[] = [
  revenueByMonth,
  rentStatus,
  rentPayments,
  arrears,
  tenants,
  rentRoll,
  deposits,
  expenses,
  expensesByCategory,
  documents,
]
